import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export interface DropdownItem {
    label: string;
    href: string;
    description?: string;
}

export interface DropdownProps {
    label: string;
    items: DropdownItem[];
    columns?: 1 | 2;
    align?: 'left' | 'right' | 'center';
}

export function Dropdown({ label, items, columns = 1, align = 'left' }: DropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors rounded-lg
                    ${isOpen ? 'text-white bg-white/10' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
                aria-expanded={isOpen}
            >
                {label}
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className={`absolute top-full ${align === 'right' ? 'right-0' : align === 'center' ? 'left-1/2 -translate-x-1/2' : 'left-0'} mt-2 z-50`}>
                    <div className={`${columns === 2 ? 'w-[520px]' : 'w-64'} bg-[#0a0a0a] border border-white/10 rounded-xl shadow-xl overflow-hidden backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-200`}>
                        <div className={`py-2 ${columns === 2 ? 'grid grid-cols-2 gap-x-0' : 'flex flex-col'}`}>
                            {items.map((item) => (
                                <a
                                    key={item.href}
                                    href={item.href}
                                    className="block px-4 py-3 hover:bg-white/5 transition-colors group"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <div className="text-sm font-medium text-slate-200 group-hover:text-white transition-colors">
                                        {item.label}
                                    </div>
                                    {item.description && (
                                        <div className="text-xs text-slate-500 group-hover:text-slate-400 mt-1 line-clamp-1">
                                            {item.description}
                                        </div>
                                    )}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
